import { useAtom } from 'jotai';
import * as React from 'react';
import {
    BodyMediumEmphasized,
    borderCurve,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    Icon,
    KeyboardCompatibleBottomSheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { selectedBillCategoriesAtom } from '../../../bills/state/BillsAtoms';
import { BillCategoryNames } from '../../types/Bill';

interface CheckboxProps {
    checked: boolean;
}

const Checkbox = ({ checked }: CheckboxProps) => {
    return (
        <View
            alignItems="center"
            justifyContent="center"
            padding={checked ? 3 : 3.5}
            borderColor="black"
            borderWidth={checked ? 0 : 1.5}
            borderRadius={8}
            backgroundColor={checked ? '$green900' : 'transparent'}
        >
            {checked ? (
                <View padding={3}>
                    <Icon icon={CheckIcon} color="$white" />
                </View>
            ) : (
                <View width={18} height={18} />
            )}
        </View>
    );
};

interface FilterSheetItemProps {
    category: string;
    isSelected: boolean;
    toggleCategory: () => void;
}

const FilterSheetItem = ({
    category,
    isSelected,
    toggleCategory,
}: FilterSheetItemProps) => {
    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            borderRadius={20}
            {...borderCurve('continuous')}
            backgroundColor={isSelected ? '$green300' : '$gray0'}
            paddingHorizontal={12}
            paddingVertical={16}
            onPress={() => {
                toggleCategory();
            }}
        >
            <BodyMediumEmphasized color={isSelected ? '$green1100' : 'black'}>
                {category}
            </BodyMediumEmphasized>
            <Checkbox checked={isSelected} />
        </XStack>
    );
};

interface FilterSheetProps {
    allBillCategories: string[];
    onClose: () => void;
}

const FilterSheet = ({ allBillCategories, onClose }: FilterSheetProps) => {
    const [selectedBillCategories, setSelectedBillCategories] = useAtom(
        selectedBillCategoriesAtom,
    );

    const toggleCategory = (category: string) => {
        const categoriesSet = new Set(selectedBillCategories);
        categoriesSet.has(category)
            ? categoriesSet.delete(category)
            : categoriesSet.add(category);
        setSelectedBillCategories(Array.from(categoriesSet));
    };

    return (
        <KeyboardCompatibleBottomSheet
            title="Filter"
            showCloseButton
            onDismiss={onClose}
        >
            <YStack gap={20}>
                <YStack gap={8}>
                    <FilterSheetItem
                        category="All"
                        isSelected={selectedBillCategories.length === 0}
                        toggleCategory={() => {
                            if (selectedBillCategories.length) {
                                setSelectedBillCategories([]);
                            }
                        }}
                    />
                    {allBillCategories.map((billCategory, index) => {
                        return (
                            <FilterSheetItem
                                key={index}
                                category={
                                    BillCategoryNames[billCategory] as string
                                }
                                isSelected={selectedBillCategories.includes(
                                    billCategory,
                                )}
                                toggleCategory={() => {
                                    toggleCategory(billCategory);
                                }}
                            />
                        );
                    })}
                </YStack>

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Done"
                    onPress={onClose}
                />
            </YStack>
        </KeyboardCompatibleBottomSheet>
    );
};

export default FilterSheet;
